import { Button, ConfigProvider, Flex, Form, Typography } from 'antd';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router';

import { confirmResetPassword } from '@/api/user';
import { AUTH_LIGHT_THEME } from '@/components/auth/authLayout';
import AuthMarketingBanner from '@/components/auth/AuthMarketingBanner';
import SchemaForm from '@/components/form/SchemaForm/SchemaForm';
import { LOGIN } from '@/constants/routes';
import { useApiMutation } from '@/hooks/api';
import { usePagePadding } from '@/hooks/usePagePadding';
import { useUrlState } from '@/hooks/useUrlState';
import { SPACE_MD } from '@/theme/buildAntdTheme';
import FormErrorAlert from '@/UI/FormErrorAlert';
import { PageTitle, SubsectionHeading } from '@/UI/typography';
import { buildResetPasswordConfirmFormSchema } from '@/utils/validation/formSchemas/authSchemas';

import type { UrlParams } from './InvitationConfirm';
import type { SchemaSection } from '@/components/form/SchemaForm/types';

const ResetPassword = () => {
  const [success, setSuccess] = useState(false);
  const { uid, token } = useParams() as unknown as UrlParams;
  const { history } = useUrlState();
  const [form] = Form.useForm();
  const [t] = useTranslation();
  const padding = usePagePadding();

  const formSchema = useMemo(() => buildResetPasswordConfirmFormSchema(t), [t]);

  const resetMutation = useApiMutation({
    mutationFn: (values: { new_password: string }) =>
      confirmResetPassword({ uid, token, new_password: values.new_password }),
    onSuccess: () => {
      setSuccess(true);
    },
  });

  const sections = useMemo<SchemaSection[]>(
    () => [
      {
        key: 'reset',
        fields: [
          { name: 'uid', type: 'hidden' },
          { name: 'token', type: 'hidden' },
          {
            name: 'new_password',
            type: 'password',
            label: t('PASSWORD_LABEL'),
            help: t('PASSWORD_HINT'),
            colSpan: 12,
          },
          {
            name: 'new_password_confirm',
            type: 'password',
            label: t('PASSWORD_CONFIRM_LABEL'),
            colSpan: 12,
          },
        ],
      },
    ],
    [t],
  );

  const onFinish = (values: {
    new_password: string;
    new_password_confirm?: string;
  }) => {
    resetMutation.mutate({ new_password: values.new_password });
  };

  return (
    <ConfigProvider theme={AUTH_LIGHT_THEME}>
      <Flex
        component="section"
        style={{
          background: 'var(--ant-color-bg-spotlight)',
          width: '100%',
          minHeight: '100vh',
          overflow: 'hidden',
          colorScheme: 'light',
        }}
      >
        <AuthMarketingBanner title={t('LOGIN_BANNER_TEXT')} />
        <Flex
          vertical
          justify="space-between"
          style={{
            background: 'var(--ant-color-bg-container)',
            position: 'relative',
            width: '32.5%',
            ...padding,
          }}
        >
          <Flex align="center" justify="space-between">
            <PageTitle>{t('RESET_PASSWORD_TITLE')}</PageTitle>
          </Flex>
          <Flex vertical gap={SPACE_MD}>
            {!success ? (
              <>
                <SubsectionHeading>
                  {t('PICK_PASSWORD_TITLE')}
                </SubsectionHeading>
                <FormErrorAlert error={resetMutation.error} />
                <SchemaForm
                  form={form}
                  formSchema={formSchema}
                  size="default"
                  name="reset-request"
                  initialValues={{ uid, token }}
                  onFinish={onFinish}
                  sections={sections}
                  footer={{
                    actions: [
                      {
                        label: t('NEW_PASSWORD_SUCCESS'),
                        htmlType: 'submit',
                        type: 'primary',
                        loading: resetMutation.isPending,
                      },
                    ],
                  }}
                />
              </>
            ) : (
              <>
                <SubsectionHeading>
                  {t('PASSWORD_CHANGED_SUCCESS')}
                </SubsectionHeading>
                <Typography.Text>
                  {t('PASSWORD_CHANGED_SUCCESS_2')}
                </Typography.Text>
                <Button type="primary" onClick={() => history.push(LOGIN)}>
                  {t('PROCEED_TITLE')}
                </Button>
              </>
            )}
          </Flex>
        </Flex>
      </Flex>
    </ConfigProvider>
  );
};

export default ResetPassword;
